import { TFunction } from 'i18next';

import { NOT_APPLICABLE } from 'constants/defaultValues';
import { SAMPLE, SAMPLE_SCAN, SCAN } from 'constants/samplePlanning';

import { DispatchType } from 'store/dispatch/types';

import { formatDateMonthDayYear } from './date';
import { isNumber } from './numUtils';
import { hasNoProducts } from './samplePlanTracking';
import { userFullName } from './userRoles';

export const getDispatchDisplayData = (
  dispatchPlan: DispatchType,
  t: TFunction,
  language: string,
) => {
  const created = dispatchPlan.created_at
    ? formatDateMonthDayYear(dispatchPlan.created_at, language)
    : NOT_APPLICABLE;
  const updated = dispatchPlan.updated_at
    ? formatDateMonthDayYear(dispatchPlan.updated_at, language)
    : NOT_APPLICABLE;
  const sampler = dispatchPlan.assigned_to_id ? userFullName(dispatchPlan.assigned_to) : null;
  const scanner = dispatchPlan.assigned_to_scan_id
    ? userFullName(dispatchPlan.assigned_to_scan)
    : null;
  const daysReady = isNumber(dispatchPlan.days_marked_ready)
    ? String(dispatchPlan.days_marked_ready)
    : null;
  const eta = dispatchPlan.estimated_sampling_date
    ? `${formatDateMonthDayYear(dispatchPlan.estimated_sampling_date, language)} ${daysReady ? `(${daysReady} ${t('daysMarkedReady')})` : ''}`
    : NOT_APPLICABLE;
  return {
    account: dispatchPlan.operation_name,
    acres: Math.round(dispatchPlan.calculated_area_acres),
    agency: dispatchPlan.agency?.name || t('none'),
    assignedTo: sampler ? `${sampler} (${dispatchPlan.assigned_to.role_name})` : NOT_APPLICABLE,
    assignedToScan: scanner
      ? `${scanner} (${dispatchPlan.assigned_to_scan.role_name})`
      : NOT_APPLICABLE,
    created,
    daysReady: daysReady || NOT_APPLICABLE,
    eta,
    field: dispatchPlan.field_name,
    pro: t(dispatchPlan.is_pro ? 'yes' : 'no'),
    readyToSample: t(dispatchPlan.ready_to_sample ? 'yes' : 'no'),
    sampler: sampler || NOT_APPLICABLE,
    scan: scanner || NOT_APPLICABLE,
    status: t(dispatchPlan.status),
    tillMapper: t(dispatchPlan.is_till_rx ? 'yes' : 'no'),
    updated,
  };
};

export const getBulkAssignBuckets = (dispatchPlans: DispatchType[]) =>
  dispatchPlans.reduce(
    (acc, plan) => {
      const { is_pro, is_till_rx, assigned_to_id, assigned_to_scan_id } = plan;
      const isProOrTillRx = is_pro || is_till_rx;
      if (!isProOrTillRx) {
        acc[SAMPLE].push(plan);
      } else if (is_till_rx && hasNoProducts(plan)) {
        acc[SCAN].push(plan);
      } else if (isProOrTillRx) {
        if (assigned_to_id && !assigned_to_scan_id) {
          acc[SCAN].push(plan);
        } else if (!assigned_to_id && assigned_to_scan_id) {
          acc[SAMPLE].push(plan);
        } else {
          acc[SAMPLE_SCAN].push(plan);
        }
      }

      return acc;
    },
    { [SAMPLE]: [], [SAMPLE_SCAN]: [], [SCAN]: [] } as { [key: string]: DispatchType[] },
  );

export const getBulkAssignTabs = (plans: { [key: string]: DispatchType[] }, t: TFunction) => {
  return [
    {
      id: 1,
      label: t('sampleOnly'),
      value: SAMPLE,
      disabled: !plans[SAMPLE].length,
    },
    {
      id: 2,
      label: t('samplePlusScan'),
      value: SAMPLE_SCAN,
      disabled: !plans[SAMPLE_SCAN].length,
    },
    {
      id: 3,
      label: t('scanOnly'),
      value: SCAN,
      disabled: !plans[SCAN].length,
    },
  ];
};
